/**
 * WeekView — lista de los 7 días de una `TrainingWeek` (Sprint 6 Bloque 4).
 *
 * Render simple: ordena las sesiones por `session_date` y renderiza un
 * `SessionCard` por cada una. El backend ya devuelve 7 entradas (una por
 * día, incluyendo `rest`), así que no inventamos huecos aquí.
 *
 * Sprint 10 S10.2b: cuando el atleta reporta fatiga, el backend crea
 * una SEGUNDA sesión en el mismo `session_date` (sub `rod_z2_corto`
 * planned). La detección de la sustituta vive en `weekViewHelpers.ts`
 * (separado para satisfacer `react-refresh/only-export-components`).
 */
import { SessionCard } from "./SessionCard";
import { _detectFatigueSubstituteIds } from "./weekViewHelpers";
import type { TrainingWeek } from "@/types/api";

export interface WeekViewProps {
  week: TrainingWeek;
  /** v14 §3: gate de render del ritmo /km. Se pasa tal cual a cada
   *  `SessionCard` (el ritmo solo se muestra si el atleta tiene reloj). */
  hasWatch?: boolean;
  /** Sesión 33 EXTENDIDA — Alba feature (Conversación 2 Alberto).
   *  Si true, `SessionCard` muestra "Sesión N" en lugar del día. El
   *  índice se calcula aquí (solo sesiones útiles cuentan; rest no). */
  flexibleScheduling?: boolean;
  /** F3 (s51): estado de exportación FIT calculado por PlanPage —
   *  se pasa tal cual a cada SessionCard. */
  fitExport?: "enabled" | "needs_vam";
  /** s53.2: callback tras marcar sesión hecha (PlanPage.refresh). */
  onSessionUpdated?: () => void;
}

export function WeekView({
  week,
  hasWatch,
  flexibleScheduling,
  fitExport,
  onSessionUpdated,
}: WeekViewProps) {
  // Defensa: ordenar por fecha por si el backend devuelve desordenado
  // (no debería, pero mejor seguro). Sprint 10 S10.2b: estabilizamos el
  // orden secundario por `id` para que la original (creada antes que
  // la sub) aparezca consistentemente ANTES de la sub en el render.
  const sortedSessions = [...week.sessions].sort((a, b) => {
    const dateCmp = a.session_date.localeCompare(b.session_date);
    if (dateCmp !== 0) return dateCmp;
    return a.id - b.id;
  });

  const fatigueSubIds = _detectFatigueSubstituteIds(sortedSessions);

  // Sesión 33 EXTENDIDA sub-bloque 5 (observación Yan): numeración
  // "Sesión N" respeta el orden cronológico del plan COMPLETO — incluye
  // rest. Cada slot del plan se renombra (1-7 por semana, lun→dom). El
  // index parte del orden de `sortedSessions` (ya ordenado por
  // session_date + id arriba), 1-indexed.

  return (
    <div
      data-testid="week-view"
      className="flex flex-col gap-2"
      role="list"
      aria-label={`Semana ${week.week_number}`}
    >
      {sortedSessions.map((session, index) => (
        <div key={session.id} role="listitem">
          <SessionCard
            session={session}
            isFatigueSubstitute={fatigueSubIds.has(session.id)}
            hasWatch={hasWatch}
            flexibleScheduling={flexibleScheduling}
            sessionIndex={index + 1}
            fitExport={fitExport}
            onSessionUpdated={onSessionUpdated}
          />
        </div>
      ))}
    </div>
  );
}
